import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { chantiers, clients, chefsEquipe, techniciens } from '../utils/mockData';
import { ArrowLeft, Save, Trash2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { Status, WorkType, Priority } from '../utils/types';
const workTypeLabels: Record<WorkType, string> = {
  signaletique_interieure: 'Signalétique intérieure',
  signaletique_exterieure: 'Signalétique extérieure',
  marquage_sol: 'Marquage au sol',
  revetement: 'Revêtement / Résine',
  peinture_decorative: 'Peinture décorative',
  mobilier: 'Installation mobilier',
  amenagement_commercial: 'Aménagement commercial',
  maintenance_preventive: 'Maintenance préventive',
  intervention_corrective: 'Intervention corrective',
  renovation_partielle: 'Rénovation partielle'
};
const statusLabels: Record<Status, string> = {
  a_planifier: 'À planifier',
  planifie: 'Planifié',
  en_preparation: 'En préparation',
  en_cours: 'En cours',
  bloque: 'Bloqué',
  en_attente_client: 'Attente client',
  termine: 'Terminé',
  facturable: 'Facturable',
  cloture: 'Clôturé'
};
export function EditChantier() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const chantier = chantiers.find(c => c.id === id);
  if (!chantier) {
    return <div className="page-container py-20 text-center" data-id="element-555">
        <p className="text-gray-500" data-id="element-556">Chantier non trouvé</p>
      </div>;
  }
  const [formData, setFormData] = useState({
    clientId: chantier.clientId,
    name: chantier.name,
    location: chantier.location,
    city: chantier.city,
    country: chantier.country,
    workType: chantier.workType,
    priority: chantier.priority,
    description: chantier.description || '',
    startDate: chantier.startDate,
    endDate: chantier.endDate,
    status: chantier.status,
    chefEquipeId: chantier.chefEquipeId,
    teamIds: chantier.team.map(t => t.id),
    progress: chantier.progress
  });
  const [isSaving, setIsSaving] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const handleSave = async () => {
    setIsSaving(true);
    await new Promise(r => setTimeout(r, 800));
    setIsSaving(false);
    navigate(`/chantiers/${id}`);
  };
  const handleDelete = async () => {
    await new Promise(r => setTimeout(r, 500));
    navigate('/chantiers');
  };
  const toggleTeam = (uid: string) => {
    setFormData(p => ({
      ...p,
      teamIds: p.teamIds.includes(uid) ? p.teamIds.filter(x => x !== uid) : [...p.teamIds, uid]
    }));
  };
  const inputClass = 'w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1DBAB4] focus:border-transparent outline-none';
  return <div className="min-h-screen bg-gray-50 pb-20 md:pb-6" data-id="element-557">
      <div className="page-container section-spacing" data-id="element-558">
        <Button variant="ghost" size="sm" className="pl-0" onClick={() => navigate(-1)} leftIcon={<ArrowLeft size={16} data-id="element-560" />} data-id="element-559">
          Retour
        </Button>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-561">
          <div data-id="element-562">
            <h1 className="heading-xl" data-id="element-563">Modifier le Chantier</h1>
            <p className="text-caption mt-1" data-id="element-564">{chantier.name}</p>
          </div>
          <Button variant="outline" className="border-red-300 text-red-600 hover:bg-red-50 w-full sm:w-auto" leftIcon={<Trash2 size={16} data-id="element-566" />} onClick={() => setShowDelete(true)} data-id="element-565">
            Supprimer
          </Button>
        </div>

        <Card className="p-4 sm:p-6 space-y-6" data-id="element-567">
          <h2 className="heading-md" data-id="element-568">Informations Générales</h2>
          <div className="space-y-4" data-id="element-569">
            <div data-id="element-570">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-571">
                Client
              </label>
              <select value={formData.clientId} onChange={e => setFormData({
              ...formData,
              clientId: e.target.value
            })} className={inputClass} data-id="element-572">
                {clients.map(c => <option key={c.id} value={c.id} data-id="element-573">
                    {c.raisonSociale}
                  </option>)}
              </select>
            </div>
            <div data-id="element-574">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-575">
                Nom du chantier
              </label>
              <input type="text" value={formData.name} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} className={inputClass} data-id="element-576" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-577">
              <div data-id="element-578">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-579">
                  Adresse
                </label>
                <input type="text" value={formData.location} onChange={e => setFormData({
                ...formData,
                location: e.target.value
              })} className={inputClass} data-id="element-580" />
              </div>
              <div data-id="element-581">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-582">
                  Ville
                </label>
                <input type="text" value={formData.city} onChange={e => setFormData({
                ...formData,
                city: e.target.value
              })} className={inputClass} data-id="element-583" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-584">
              <div data-id="element-585">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-586">
                  Type de travaux
                </label>
                <select value={formData.workType} onChange={e => setFormData({
                ...formData,
                workType: e.target.value as WorkType
              })} className={inputClass} data-id="element-587">
                  {Object.entries(workTypeLabels).map(([k, v]) => <option key={k} value={k} data-id="element-588">
                      {v}
                    </option>)}
                </select>
              </div>
              <div data-id="element-589">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-590">
                  Priorité
                </label>
                <select value={formData.priority} onChange={e => setFormData({
                ...formData,
                priority: e.target.value as Priority
              })} className={inputClass} data-id="element-591">
                  <option value="normal" data-id="element-592">Normal</option>
                  <option value="urgent" data-id="element-593">Urgent</option>
                  <option value="critique" data-id="element-594">Critique</option>
                </select>
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-4 sm:p-6 space-y-6" data-id="element-595">
          <h2 className="heading-md" data-id="element-596">Statut & Planning</h2>
          <div className="space-y-4" data-id="element-597">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-598">
              <div data-id="element-599">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-600">
                  Date de début
                </label>
                <input type="date" value={formData.startDate} onChange={e => setFormData({
                ...formData,
                startDate: e.target.value
              })} className={inputClass} data-id="element-601" />
              </div>
              <div data-id="element-602">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-603">
                  Date de fin
                </label>
                <input type="date" value={formData.endDate} onChange={e => setFormData({
                ...formData,
                endDate: e.target.value
              })} className={inputClass} data-id="element-604" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-605">
              <div data-id="element-606">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-607">
                  Statut
                </label>
                <select value={formData.status} onChange={e => setFormData({
                ...formData,
                status: e.target.value as Status
              })} className={inputClass} data-id="element-608">
                  {Object.entries(statusLabels).map(([k, v]) => <option key={k} value={k} data-id="element-609">
                      {v}
                    </option>)}
                </select>
              </div>
              <div data-id="element-610">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-611">
                  Progression ({formData.progress}%)
                </label>
                <input type="range" min="0" max="100" value={formData.progress} onChange={e => setFormData({
                ...formData,
                progress: parseInt(e.target.value)
              })} className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#1DBAB4]" data-id="element-612" />
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-4 sm:p-6 space-y-6" data-id="element-613">
          <h2 className="heading-md" data-id="element-614">Équipe</h2>
          <div className="space-y-4" data-id="element-615">
            <div data-id="element-616">
              <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-617">
                Chef d'équipe
              </label>
              <div className="space-y-2" data-id="element-618">
                {chefsEquipe.map(c => <label key={c.id} className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-all ${formData.chefEquipeId === c.id ? 'border-[#1DBAB4] bg-teal-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-619">
                    <input type="radio" name="chef" checked={formData.chefEquipeId === c.id} onChange={() => setFormData({
                  ...formData,
                  chefEquipeId: c.id
                })} className="text-[#1DBAB4]" data-id="element-620" />
                    <img src={c.avatar} alt={c.name} className="w-10 h-10 rounded-full" data-id="element-621" />
                    <div data-id="element-622">
                      <p className="font-medium text-gray-900" data-id="element-623">{c.name}</p>
                    </div>
                  </label>)}
              </div>
            </div>
            <div data-id="element-624">
              <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-625">
                Techniciens ({formData.teamIds.length})
              </label>
              <div className="space-y-2" data-id="element-626">
                {techniciens.map(t => <label key={t.id} className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-all ${formData.teamIds.includes(t.id) ? 'border-[#1DBAB4] bg-teal-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-627">
                    <input type="checkbox" checked={formData.teamIds.includes(t.id)} onChange={() => toggleTeam(t.id)} className="text-[#1DBAB4] rounded" data-id="element-628" />
                    <img src={t.avatar} alt={t.name} className="w-10 h-10 rounded-full" data-id="element-629" />
                    <div data-id="element-630">
                      <p className="font-medium text-gray-900" data-id="element-631">{t.name}</p>
                    </div>
                  </label>)}
              </div>
            </div>
          </div>
        </Card>

        <div className="sticky bottom-20 md:bottom-0 md:static bg-white md:bg-transparent p-4 md:p-0 -mx-4 md:mx-0 border-t md:border-t-0 border-gray-200 md:border-0 shadow-lg md:shadow-none flex items-center justify-end gap-3" data-id="element-632">
          <Button variant="outline" onClick={() => navigate(-1)} className="flex-1 sm:flex-initial" data-id="element-633">
            Annuler
          </Button>
          <Button onClick={handleSave} isLoading={isSaving} leftIcon={<Save size={18} data-id="element-635" />} className="flex-1 sm:flex-initial" data-id="element-634">
            Enregistrer
          </Button>
        </div>

        {showDelete && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowDelete(false)} data-id="element-636">
            <motion.div initial={{
          scale: 0.9
        }} animate={{
          scale: 1
        }} className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()} data-id="element-637">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-638">
                <Trash2 className="w-6 h-6 text-red-600" data-id="element-639" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 text-center mb-2" data-id="element-640">
                Supprimer le chantier ?
              </h3>
              <p className="text-gray-600 text-center mb-6" data-id="element-641">
                Cette action est irréversible.
              </p>
              <div className="flex gap-3" data-id="element-642">
                <Button variant="outline" className="flex-1" onClick={() => setShowDelete(false)} data-id="element-643">
                  Annuler
                </Button>
                <Button className="flex-1 bg-red-600 hover:bg-red-700" onClick={handleDelete} data-id="element-644">
                  Supprimer
                </Button>
              </div>
            </motion.div>
          </motion.div>}
      </div>
    </div>;
}